import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config";

const CreateCategoryForm = () => {
  const [categories, setCategories] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const token = localStorage.getItem("token");

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}getCategoriesList`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === "success") {
        setCategories(response.data.data);
      } else {
        showMessage(
          response.data.message || "Failed to fetch categories.",
          "error"
        );
      }
    } catch (error) {
      showMessage("Error fetching categories.", "error");
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const showMessage = (message, type = "success") => {
    setResponseMessage({ text: message, type });
    setTimeout(() => setResponseMessage(""), 3000);
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!token) {
      showMessage("Authorization token is missing.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("categorie_name", categoryName);

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_URL}createCategories`,
        formData,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 201) {
        showMessage(response.data.message || "Category created successfully!");
        setCategoryName("");
        fetchCategories();
      } else {
        showMessage(
          response.data.message || "Failed to create category.",
          "error"
        );
      }
    } catch (error) {
      showMessage("Error creating category.", "error");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (categoryId) => {
    try {
      setIsLoading(true);
      const response = await axios.delete(
        `${API_URL}deleteCategorie/${categoryId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === "success") {
        showMessage("Category deleted successfully!");
        fetchCategories();
      } else {
        showMessage(
          response.data.message || "Failed to delete category.",
          "error"
        );
      }
    } catch (error) {
      showMessage("Error deleting category.", "error");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedCategory) return;

    try {
      setIsLoading(true);
      const response = await axios.put(
        `${API_URL}categories/${selectedCategory.id}`,
        { categorie_name: selectedCategory.name },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "success") {
        showMessage("Category updated successfully!");
        setSelectedCategory(null);
        setShowModal(false);
        fetchCategories();
      } else {
        showMessage(
          response.data.message || "Failed to update category.",
          "error"
        );
      }
    } catch (error) {
      showMessage("Error updating category.", "error");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
            Category Management
          </h2>

          {/* Create Category Form */}
          <form onSubmit={handleCreate} className="mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <input
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Enter category name"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
                required
              />
              <button
                type="submit"
                disabled={isLoading}
                className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-800 text-white rounded-lg hover:from-green-700 hover:to-green-900 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Creating..." : "Add Category"}
              </button>
            </div>
          </form>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <div
                key={category.id}
                className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-800">
                    {category.name}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowModal(true);
                      }}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Edit Modal */}
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold mb-4">Edit Category</h3>
                <input
                  type="text"
                  value={selectedCategory?.name || ""}
                  onChange={(e) =>
                    setSelectedCategory({
                      ...selectedCategory,
                      name: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEdit}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Response Message */}
          {responseMessage && (
            <div
              className={`mt-4 p-4 rounded-lg ${
                responseMessage.type === "error"
                  ? "bg-red-100 text-red-800"
                  : "bg-green-100 text-green-800"
              } transition-all duration-500`}
            >
              {responseMessage.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateCategoryForm;
